'use client';
import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import { ArrowRight, Lock, Sparkles } from 'lucide-react';

const rotatingPhrases = [
  'stronger belief in yourself',
  'more confident way of thinking',
  'bolder approach to your goals',
  'calmer, more powerful mindset',
  'stronger sense of self-trust',
  'more courageous inner voice',
  'more grounded confidence',
  'version of you that follows through',
];

export default function LandingHero() {
  const [phraseIndex, setPhraseIndex] = useState(0);
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    const interval = setInterval(() => {
      setVisible(false);
      setTimeout(() => {
        setPhraseIndex((i) => (i + 1) % rotatingPhrases?.length);
        setVisible(true);
      }, 300);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  return (
    <section className="pt-32 pb-20 px-6 max-w-screen-lg mx-auto text-center">
      {/* Eyebrow */}
      <div className="inline-flex items-center gap-2 bg-white border border-[#EAE6E0] rounded-full px-4 py-2 text-sm font-medium text-[#2A9D8F] mb-8 shadow-sm">
        <Sparkles size={14} />
        <span>Personalised to how your mind actually works</span>
      </div>
      {/* Headline */}
      <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#1C1917] mb-6 text-balance leading-tight">
        Create a{' '}
        <span
          className="text-[#2A9D8F] inline-block transition-opacity duration-300"
          style={{ opacity: visible ? 1 : 0 }}
        >
          {rotatingPhrases?.[phraseIndex]}
        </span>
        <br className="hidden sm:block" />
        {' '}— starting in 60 seconds.
      </h1>
      {/* Subheading */}
      <p className="text-lg sm:text-xl text-[#6B6560] max-w-2xl mx-auto mb-10 leading-relaxed">
        blv uses evidence-informed micro-resets and a personalised practice to rewire
        the inner dialogue that holds you back. Tools that adapt to you — from day one.
      </p>
      {/* CTA group */}
      <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8">
        <Link href="/onboarding-questionnaire" className="btn-primary text-lg w-full sm:w-auto">
          Create my personalised reset
          <ArrowRight size={18} />
        </Link>
        <Link
          href="/personalised-plan-preview"
          className="text-sm text-[#6B6560] hover:text-[#2A9D8F] transition-colors underline underline-offset-4"
        >
          See a sample plan first
        </Link>
      </div>
      {/* Privacy reassurance */}
      <div className="flex items-center justify-center gap-2 text-sm text-[#9B9590]">
        <Lock size={13} />
        <span>Private by design. Your data is never shared. Takes under 3 minutes.</span>
      </div>
      {/* Hero visual */}
      <div className="mt-16 relative mx-auto max-w-2xl">
        <div className="card-elevated rounded-3xl p-8 text-left relative overflow-hidden">
          {/* Decorative background blob */}
          <div
            className="absolute -top-12 -right-12 w-48 h-48 rounded-full pointer-events-none"
            style={{ background: 'radial-gradient(circle, rgba(42,157,143,0.08) 0%, transparent 70%)' }}
          />
          <div className="flex items-start gap-4 mb-6">
            <div className="w-10 h-10 rounded-full bg-[#2A9D8F]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
              <Sparkles size={18} className="text-[#2A9D8F]" />
            </div>
            <div>
              <p className="text-xs font-semibold text-[#2A9D8F] uppercase tracking-widest mb-1">Your Pattern</p>
              <h3 className="text-xl font-bold text-[#1C1917]">The Quiet Achiever</h3>
            </div>
          </div>
          <p className="text-[#4A4540] leading-relaxed mb-6 text-sm sm:text-base">
            You set high standards and often meet them — yet a persistent inner voice questions
            whether it was "really" good enough. Your nervous system is wired for vigilance, not rest.
            blv will help you interrupt that loop and build a quieter, more trusting relationship with yourself.
          </p>
          <div className="flex flex-wrap gap-2 mb-6">
            {['High Standards', 'Inner Critic', 'Quiet Doubt']?.map((tag) => (
              <span key={`hero-tag-${tag}`} className="tag-chip">{tag}</span>
            ))}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="reset-card">
              <p className="text-xs font-semibold text-[#2A9D8F] uppercase tracking-widest mb-2">10-Second Reset</p>
              <p className="text-sm text-[#4A4540] leading-relaxed">
                Soften your jaw, take one slow breath, and say quietly: <em>"I am already enough."</em>
              </p>
            </div>
            <div className="reset-card">
              <p className="text-xs font-semibold text-[#52B788] uppercase tracking-widest mb-2">60-Second Reset</p>
              <p className="text-sm text-[#4A4540] leading-relaxed">
                Close your eyes. Breathe in for 4, hold for 4, out for 6. Notice where tension lives — and let it soften.
              </p>
            </div>
          </div>
          <div className="mt-4 text-xs text-[#9B9590] italic">
            ↑ This is a sample — yours will be tailored to your answers.
          </div>
        </div>
      </div>
    </section>
  );
}